// Access-token rotation that is safe across tabs.
//
// Every tab on the origin shares one httpOnly refresh cookie per audience, and
// the API spends a refresh token the moment it rotates it (single-use, no
// reuse grace — RefreshTokenService.rotate in apps/api). A per-fetcher
// in-flight promise only dedupes inside one JS context, so two tabs — or two
// fetchers bound to one audience in the same tab — could POST /auth/refresh
// with the same cookie: one won, the loser's 401 cleared tt_token + tt_user,
// and the winner then wrote tt_token back alone. Every reload read that
// token-without-user state as signed out.
//
// So a rotation here:
// - runs under a per-audience Web Lock shared by every tab and fetcher, so each
//   POST carries the cookie the previous rotation set;
// - re-reads storage first, and reuses a token another tab already stored
//   instead of spending the cookie again;
// - writes a token only while the session's cached user is still stored, so a
//   sign-out during the POST is never undone;
// - after a failed POST, adopts a token another tab stored meanwhile before
//   calling the session gone — and, where Web Locks are missing, first gives
//   such a token a moment to arrive.
//
// Kept free of Vite-only syntax (import.meta): the contracts jest suite loads
// this module directly (packages/contracts/src/__tests__/auth-cross-tab-refresh.test.ts).

/** The localStorage keys that hold one audience's browser session. */
export interface SessionKeys {
  /** Short-lived access JWT. */
  tokenKey: string
  /** Refresh token for an audience that keeps it in localStorage, else null. */
  refreshKey: string | null
  /**
   * Cached user. The session exists while this is stored — its access token
   * may be missing (expired or cleared) and is minted again from the refresh
   * cookie. `null`: the audience caches no user.
   */
  userKey: string | null
}

/** What one POST /auth/refresh produced. It must never touch storage. */
export type RefreshAttempt =
  | { ok: true; accessToken: string; refreshToken?: string }
  // `transient`: no verdict on the session (offline, 429, 5xx, unreadable body).
  | { ok: false; transient: boolean }

/** What the caller does after its 401. */
export type RefreshOutcome =
  // Retry with this token — freshly rotated, or one another tab stored.
  | { status: 'rotated'; token: string }
  // No session is left in storage: clear it and signal sign-out.
  | { status: 'signed-out' }
  // No verdict: fail this call only and keep the session for the next one.
  | { status: 'unavailable' }

/** How long a 401 waits behind another tab's rotation before failing that call. */
export const REFRESH_LOCK_WAIT_MS = 10_000

/**
 * Without the cross-tab lock, how long a refused rotation waits for the token of
 * a tab that spent the same cookie a moment earlier to reach this tab's storage.
 */
export const REFUSAL_GRACE_MS = 1_500

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

function readSession(keys: SessionKeys): { token: string | null; hasUser: boolean } {
  return {
    token: localStorage.getItem(keys.tokenKey),
    hasUser: !keys.userKey || localStorage.getItem(keys.userKey) !== null,
  }
}

// Fallback where Web Locks are missing (insecure origins such as a LAN IP over
// http, very old browsers, the jest harness): serializes the fetchers of this
// tab only. The storage re-checks then cover the cross-tab case as far as
// they can.
const localTails = new Map<string, Promise<unknown>>()

function withLocalLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const run = (localTails.get(name) ?? Promise.resolve()).then(fn)
  localTails.set(name, run.catch(() => undefined))
  return run
}

// `fn` is told whether it holds the cross-tab lock (true) or only this tab's.
async function withRefreshLock(
  name: string,
  fn: (crossTab: boolean) => Promise<RefreshOutcome>,
): Promise<RefreshOutcome> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (!locks) return withLocalLock(name, () => fn(false))
  const wait = new AbortController()
  const timer = setTimeout(() => wait.abort(), REFRESH_LOCK_WAIT_MS)
  let granted = false
  try {
    return await locks.request(name, { signal: wait.signal }, () => {
      granted = true
      clearTimeout(timer)
      return fn(true)
    })
  } catch (err) {
    if (granted) throw err
    // Another tab is stuck mid-rotation (frozen, a hung request): fail this
    // call instead of hanging it, and keep the session.
    if (wait.signal.aborted) return { status: 'unavailable' }
    // Locks refused (e.g. an opaque origin): serialize within this tab instead.
    return withLocalLock(name, () => fn(false))
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Rotate the access token after a request sent with `staleToken` got a 401.
 * `attempt` performs one POST /auth/refresh; this function owns every read and
 * write of the session keys.
 */
export function rotateSessionToken(
  keys: SessionKeys,
  staleToken: string | null,
  attempt: () => Promise<RefreshAttempt>,
): Promise<RefreshOutcome> {
  return withRefreshLock(`tt-auth-refresh:${keys.tokenKey}`, async (crossTab) => {
    const before = readSession(keys)
    // Signed out (here or in another tab) since the request went out, or a
    // token with no user: nothing to refresh into.
    if (!before.hasUser) return { status: 'signed-out' }
    // Another tab or fetcher already rotated — retry with its token.
    if (before.token && before.token !== staleToken) return { status: 'rotated', token: before.token }

    const result = await attempt()
    // Without the cross-tab lock, a tab that sent the same cookie a moment
    // earlier may have won: our refusal usually lands before its token reaches
    // this tab's storage. Wait for it before reading the verdict.
    if (!crossTab && !result.ok && !result.transient) await sleep(REFUSAL_GRACE_MS)
    const after = readSession(keys)
    // Signed out while the POST was out: never write the session back.
    if (!after.hasUser) return { status: 'signed-out' }
    // A token stored meanwhile (a sign-in, or a tab rotating without Web
    // Locks) is at least as fresh as ours, and outlives our refusal.
    if (after.token && after.token !== before.token) return { status: 'rotated', token: after.token }
    if (!result.ok) return result.transient ? { status: 'unavailable' } : { status: 'signed-out' }

    localStorage.setItem(keys.tokenKey, result.accessToken)
    if (keys.refreshKey && result.refreshToken) localStorage.setItem(keys.refreshKey, result.refreshToken)
    return { status: 'rotated', token: result.accessToken }
  })
}
